import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", business_name: "", email: "", password: "",
    role: params.get("role") === "seller" ? "seller" : "buyer",
    agreement: false,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isSeller = form.role === "seller";

  const submit = async (e) => {
    e.preventDefault();
    if (isSeller && !form.agreement) {
      setError("You must agree to the Alteza Marketplace Seller Agreement to continue.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload = isSeller
        ? { name: form.name, business_name: form.business_name, email: form.email, password: form.password, role: form.role, agreement_accepted: true }
        : { name: form.name, email: form.email, password: form.password, role: form.role };
      const user = await register(payload);
      navigate(user.role === "seller" ? "/welcome" : "/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="register-page" className="max-w-md mx-auto px-6 py-24 lg:py-32">
      <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-4">◦ Join Alteza</div>
      <h1 className="font-serif text-5xl lg:text-6xl tracking-tight font-light">Create an account.</h1>
      <form onSubmit={submit} className="mt-12 space-y-6">
        <div>
          <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3 block">I'm here to</label>
          <div className="grid grid-cols-2 gap-3">
            {["buyer", "seller"].map((r) => (
              <button
                key={r} type="button" data-testid={`role-${r}`}
                onClick={() => setForm({ ...form, role: r })}
                className={`px-4 py-3 text-sm border rounded-sm transition-colors ${form.role === r ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/50"}`}
              >
                {r === "buyer" ? "Discover & buy" : "Sell my craft"}
              </button>
            ))}
          </div>
        </div>

        {isSeller && (
          <div className="fade-in">
            <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Shop / Business name</label>
            <input
              data-testid="register-business-name"
              required={isSeller}
              value={form.business_name}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              placeholder="e.g. Maison Lumi"
              className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors"
            />
            <p className="text-xs text-muted-foreground mt-2">This is what shoppers will see on your products and shop page.</p>
          </div>
        )}

        <div>
          <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">{isSeller ? "Your name (private)" : "Name"}</label>
          <input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
          {isSeller && <p className="text-xs text-muted-foreground mt-2">For our records and messages — never shown publicly.</p>}
        </div>

        <div>
          <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Email</label>
          <input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
        </div>
        <div>
          <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Password</label>
          <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
        </div>

        {error && <div data-testid="register-error" className="text-sm text-destructive">{error}</div>}

        {isSeller && (
          <div className="fade-in border border-border rounded-sm p-5 bg-secondary/40">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                data-testid="register-agreement"
                type="checkbox"
                required
                checked={form.agreement}
                onChange={(e) => setForm({ ...form, agreement: e.target.checked })}
                className="mt-1 w-4 h-4 flex-shrink-0 accent-primary"
              />
              <span className="text-sm leading-relaxed">
                I have read and agree to the{" "}
                <Link
                  to="/seller-agreement"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="seller-agreement-link"
                  className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium"
                >
                  Seller Agreement
                </Link>.
              </span>
            </label>
            <p className="text-xs text-muted-foreground mt-3 leading-relaxed pl-7">
              By submitting this form, you confirm that you understand Alteza Marketplace takes an
              8% commission on completed sales and that you are responsible for your own product
              listings, pricing, inventory, fulfillment, shipping, and customer service.
            </p>
          </div>
        )}

        <button data-testid="register-submit" disabled={loading} className="btn-primary w-full py-3.5 text-sm tracking-wide rounded-sm disabled:opacity-60">
          {loading ? "Creating…" : isSeller ? "Open my shop" : "Create account"}
        </button>
        <div className="text-sm text-muted-foreground text-center pt-4">
          Already have one? <Link to="/login" className="text-foreground hover:text-primary transition-colors">Sign in →</Link>
        </div>
      </form>
    </div>
  );
}
